import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../../i18n/translationKeys';
import { IconClose } from '../../../assets/icons/IconClose';
import { IconFileGeneric } from '../../../assets/icons/IconFileGeneric';
import { IconFileImage } from '../../../assets/icons/IconFileImage';
import { IconFilePdf } from '../../../assets/icons/IconFilePdf';
import { LinkButton } from '../../../ui/LinkButton/LinkButton';

interface AttachmentListProps {
    files: string[];
    onDelete: (filename: string) => void;
    onAdd?: () => void;
    onPreview?: (filename: string) => void;
    previewableFiles?: Set<string>;
    disabled?: boolean;
    /** Prefix used to build per-row data-testid attributes: `{rowTestIdPrefix}-{filename}`. */
    rowTestIdPrefix?: string;
    /** Prefix used to build per-row delete button data-testid: `{deleteBtnTestIdPrefix}-{filename}`. */
    deleteBtnTestIdPrefix?: string;
    /** data-testid placed on the "Add attachment" button. */
    addBtnTestId?: string;
    /** data-testid placed on the preview button (same value on every row — used when only one row is expected). */
    previewBtnTestId?: string;
}

function getFileIcon(filename: string): React.FC<React.SVGProps<SVGSVGElement>> {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext && ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return IconFileImage;
    if (ext === 'pdf') return IconFilePdf;
    return IconFileGeneric;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({
    files,
    onDelete,
    onAdd,
    onPreview,
    previewableFiles,
    disabled = false,
    rowTestIdPrefix,
    deleteBtnTestIdPrefix,
    addBtnTestId,
    previewBtnTestId,
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-s">
            {files.length > 0 && (
                <ul className="flex flex-col gap-s">
                    {files.map((filename) => {
                        const FileIcon = getFileIcon(filename);
                        const canPreview = onPreview && (!previewableFiles || previewableFiles.has(filename));
                        const rowTestId = rowTestIdPrefix ? `${rowTestIdPrefix}-${filename}` : undefined;
                        const deleteTestId = deleteBtnTestIdPrefix
                            ? `${deleteBtnTestIdPrefix}-${filename}`
                            : undefined;
                        return (
                            <li
                                key={filename}
                                data-testid={rowTestId}
                                className="flex items-center justify-between gap-s px-m py-s rounded-md bg-surface-subtle"
                            >
                                {canPreview ? (
                                    <button
                                        type="button"
                                        onClick={() => canPreview && onPreview(filename)}
                                        data-testid={previewBtnTestId}
                                        className="flex items-center gap-s min-w-0 cursor-pointer group"
                                    >
                                        <FileIcon className="w-4 h-4 shrink-0 text-primary-700" />
                                        <span className="text-s text-primary-1200 truncate group-hover:underline">{filename}</span>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-s min-w-0">
                                        <FileIcon className="w-4 h-4 shrink-0 text-primary-700" />
                                        <span className="text-s text-primary-1200 truncate">{filename}</span>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onDelete(filename)}
                                    disabled={disabled}
                                    data-testid={deleteTestId}
                                    className={`-m-xs p-xs shrink-0 transition-colors ${disabled
                                        ? 'text-primary-500 cursor-not-allowed'
                                        : 'text-primary-700 hover:text-error cursor-pointer'
                                        }`}
                                    aria-label={t(TranslationKey.Common.DeleteAriaLabel, { filename })}
                                >
                                    <IconClose className="w-4 h-4" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {onAdd && (
                <LinkButton
                    variant="navigation"
                    size="s"
                    weight="medium"
                    onClick={onAdd}
                    disabled={disabled}
                    data-testid={addBtnTestId}
                    className="self-center"
                >
                    {t(TranslationKey.CustomerFollowUp.Email.AddFile)}
                </LinkButton>
            )}
        </div>
    );
};
